.hero {
  width: 100%;
  max-width: 96rem;
  margin: 0 auto;
  padding-top: 4.5rem;
  background: var(--color-background);
}

.heroMain {
  position: relative;
  min-height: 42rem;
  padding: 7rem 3.5rem 6rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;
  gap: 2rem;
  overflow: hidden;
}

.embossedVisual {
  position: absolute;
  z-index: 0;
  top: 50%;
  right: 3.5rem;
  width: min(39vw, 34rem);
  aspect-ratio: 1;
  display: grid;
  place-items: center;
  color: #f3f5f8;
  pointer-events: none;
  transform: translateY(-50%);
}

.markRing {
  position: absolute;
  inset: 5%;
  border: 1px solid rgb(255 255 255 / 0.86);
  border-radius: 50%;
  box-shadow:
    0 0 0 1px rgb(15 23 42 / 0.018),
    inset 0 1px 2px rgb(255 255 255 / 0.9),
    0 8px 32px rgb(57 75 110 / 0.035);
}

.heroMark {
  position: relative;
  z-index: 1;
  font-size: min(34vw, 28rem) !important;
  color: #f1f3f7;
  filter:
    drop-shadow(-2px -2px 2px rgb(255 255 255 / 0.96))
    drop-shadow(2px 3px 3px rgb(62 84 122 / 0.13))
    drop-shadow(0 14px 24px rgb(62 84 122 / 0.08));
}

.content {
  position: relative;
  z-index: 2;
  max-width: 48rem;
  container-type: inline-size;
}

.title {
  max-width: 48rem;
  // Capped by the column width so the longest line never reaches the visual.
  font-size: min(clamp(3.75rem, 5.4vw, 4.875rem), 11.5cqi);
}

.period {
  color: var(--color-primary);
}

.subtitle {
  max-width: 36rem;
  margin: 0.75rem 0 0;
  font-size: 1.1875rem;
  line-height: 1.55;
  text-wrap: pretty;
}

.actions {
  margin-top: 2.5rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.25rem;
}

.note {
  margin: 1.25rem 0 0;
}

.primaryAction,
.secondaryAction {
  text-decoration: none;
}

.primaryAction {
  min-width: 9.5rem;
}

.secondaryAction {
  padding-inline: 0.75rem;
}

.visual {
  position: absolute;
  z-index: 1;
  top: 50%;
  right: 3.5rem;
  width: min(34vw, 30rem);
  display: grid;
  place-items: center;
  transform: translateY(-50%);
}

.aside {
  position: relative;
  z-index: 2;
  width: min(100%, 30rem);
}

.hero[data-has-aside="true"] {
  .heroMain {
    min-height: 46rem;
    padding-block: 6rem;
    grid-template-columns: minmax(0, 1fr) minmax(29rem, 0.72fr);
    gap: 4rem;
  }

  .content,
  .title {
    max-width: 48rem;
  }

  .subtitle {
    max-width: 36rem;
  }

  .visual {
    right: 2rem;
    width: min(42vw, 40rem);
  }

  .embossedVisual {
    right: -1rem;
    width: min(48vw, 40rem);
    opacity: 0.72;
  }
}

@media (max-width: 1180px) {
  .heroMain {
    min-height: 40rem;
    padding-inline: 2rem;
    grid-template-columns: minmax(0, 1fr);
  }

  .title {
    font-size: min(clamp(3.75rem, 7vw, 4.75rem), 11.5cqi);
  }

  .hero[data-has-aside="true"] {
    .heroMain {
      grid-template-columns: minmax(0, 1fr) minmax(27rem, 0.8fr);
      gap: 2rem;
    }

    .visual {
      right: 0;
      width: min(40vw, 28rem);
    }

    .embossedVisual {
      right: -6rem;
      width: min(52vw, 34rem);
    }
  }

}

@media (max-width: 900px) {
  .hero {
    padding-top: 4.5rem;
  }

  .heroMain {
    min-height: auto;
    padding: 5rem 1.5rem 1.5rem;
    grid-template-columns: 1fr;
  }

  .content {
    max-width: 43rem;
  }

  .visual {
    position: relative;
    top: auto;
    right: auto;
    width: min(100%, 35rem);
    margin: -2rem auto -4rem;
    opacity: 0.82;
    transform: none;
  }

  .embossedVisual {
    top: 46%;
    right: -8rem;
    width: min(72vw, 32rem);
    opacity: 0.7;
  }

  .hero[data-has-aside="true"] {
    .heroMain {
      min-height: auto;
      padding-block: 5rem 3.5rem;
      grid-template-columns: 1fr;
      gap: 2.5rem;
    }

    .visual {
      right: auto;
      width: min(100%, 35rem);
      margin: 0 auto;
      opacity: 1;
    }

    .embossedVisual {
      top: 34%;
      right: -9rem;
      width: min(82vw, 34rem);
    }
  }

}

@media (max-width: 640px) {
  .heroMain {
    padding-top: 3.75rem;
  }

  .title {
    font-size: clamp(3rem, 14vw, 3.75rem);
    line-height: 0.96;

    br {
      display: none;
    }
  }

  .subtitle {
    font-size: 1.0625rem;
  }

  .actions {
    align-items: stretch;
    flex-direction: column;
  }

  .primaryAction,
  .secondaryAction {
    width: 100%;
  }

  .visual {
    margin-top: -1rem;
    margin-bottom: -2.5rem;
  }

  .embossedVisual {
    top: 37%;
    right: -9rem;
    width: 24rem;
  }

  .heroMark {
    font-size: 20rem !important;
  }

}

/* Bleed layout: a wide diagram anchored from the middle of the hero that runs off the right
   edge of the screen (the page clips it). Cards and forms keep the default layout above. */
.hero[data-aside-layout="bleed"] {
  .heroMain {
    overflow-x: visible;
    overflow-y: clip;
  }

  .aside {
    width: 100%;
  }

  .visual {
    right: auto;
    left: 50%;
    width: min(58vw, 58rem);
  }

  .embossedVisual {
    display: none;
  }

  @media (max-width: 1180px) {
    .visual {
      left: 50%;
      width: 58vw;
    }
  }

  @media (max-width: 900px) {
    .visual {
      left: auto;
      /* Wider than the column: the diagram lines up with the text and runs off the right. */
      justify-self: start;
      width: min(125%, 50rem);
      margin: 0;
      opacity: 1;
    }
  }
}
